import { atom } from "jotai";
import { atomFamily, atomWithRefresh, loadable } from "jotai/utils";

import { getCache } from "../_interop/cache";
import { $accessTokenFunc, $loggedInUser, $loginContextGuard } from "../authenication";
import { $docsApi } from "../docsapi/docsapi.atom";
import { type Workspace } from "./workspace.types";

const isCompleteWorkspace = (workspace: Partial<Workspace>): workspace is Workspace => {
	return (
		typeof workspace.id === "string" &&
		typeof workspace.name === "string" &&
		typeof workspace.host === "string" &&
		typeof workspace.slug === "string" &&
		typeof workspace.owner === "boolean" &&
		["owner", "member", "guest"].includes(workspace.role ?? "")
	);
};

// Optimistic overrides for workspace fields (e.g. avatarUrl updates)
export const $workspaceOverrides = atom(
	undefined as Record<string, Partial<Workspace>> | undefined
);
$workspaceOverrides.debugPrivate = true;

export const $workspaces = atom((get) => {
	const workspaces: Record<string, Workspace> = {};

	const cacheWorkspaces = get($cacheWorkspaces);
	const remoteWorkspaces = get($remoteWorkspaces);
	const overrides = get($workspaceOverrides);

	const aggregated = [cacheWorkspaces, remoteWorkspaces];

	// Start with cache workspaces
	if (cacheWorkspaces.state === "hasData") {
		Object.assign(workspaces, cacheWorkspaces.data);
	}

	// Merge remote workspaces
	if (remoteWorkspaces.state === "hasData") {
		Object.entries(remoteWorkspaces.data).forEach(([id, remoteWorkspace]) => {
			workspaces[id] = remoteWorkspace;
		});
	}

	// Apply optimistic overrides (e.g. avatar URL changes before next server refetch)
	if (overrides) {
		Object.entries(overrides).forEach(([id, override]) => {
			if (workspaces[id]) {
				workspaces[id] = { ...workspaces[id], ...override };
			} else if (isCompleteWorkspace(override)) {
				workspaces[id] = override;
			}
		});
	}

	const workspacesArray = Object.values(workspaces);

	return {
		isLoading: aggregated.some((a) => a.state === "loading"),
		isSuccess: aggregated.every((a) => a.state === "hasData"),
		isError: aggregated.some((a) => a.state === "hasError"),
		error: aggregated.find((a) => a.state === "hasError")?.error,
		data: workspaces,
		workspaces: workspacesArray,
		ownedWorkspaces: workspacesArray.filter((workspace) => workspace.owner === true),
		workspacesWithAccess: workspacesArray.filter(
			(workspace) => workspace.role === "owner" || workspace.role === "member"
		),
	};
});
$workspaces.debugLabel = "workspaces";

export const $workspacesEntries = atom((get) => Object.entries(get($workspaces).workspaces));

const $cacheWorkspacesValue = atomWithRefresh(async (get) => {
	await get($loginContextGuard);
	return getCache().workspaces.getAll();
});
$cacheWorkspacesValue.debugLabel = "cacheWorkspacesValue";

const $cacheWorkspaces = loadable($cacheWorkspacesValue);
$cacheWorkspaces.debugLabel = "cacheWorkspaces";

const $remoteWorkspacesValue = atomWithRefresh(async (get): Promise<Record<string, Workspace>> => {
	const docsApi = get($docsApi);

	const token = await get($accessTokenFunc).getAccessTokenSilently?.();
	if (!token) {
		return {};
	}

	return docsApi.workspaces.list(token);
});
$remoteWorkspacesValue.debugLabel = "remoteWorkspacesValue";

const $remoteWorkspaces = loadable($remoteWorkspacesValue);
$remoteWorkspaces.debugLabel = "remoteWorkspaces";

export const $refreshWorkspaces = atom(null, (_get, set) => {
	set($cacheWorkspacesValue);
	set($remoteWorkspacesValue);
});
$refreshWorkspaces.debugLabel = "refreshWorkspaces";

export const $upsertWorkspace = atom(null, async (get, set, workspace: Workspace) => {
	await getCache().workspaces.upsert(workspace);

	const currentOverrides = get($workspaceOverrides) ?? {};
	set($workspaceOverrides, {
		...currentOverrides,
		[workspace.id]: { ...currentOverrides[workspace.id], ...workspace },
	});

	set($cacheWorkspacesValue);
});
$upsertWorkspace.debugLabel = "upsertWorkspace";

export const $personalWorkspace = atom((get) => {
	const loadableRemoteWorkspaces = get($remoteWorkspaces);
	const user = get($loggedInUser);

	if (!user || !user.sub) {
		return undefined;
	}

	if (loadableRemoteWorkspaces.state !== "hasData") {
		return undefined;
	}

	const remoteWorkspaces = loadableRemoteWorkspaces.data;

	return remoteWorkspaces[user.sub] ?? undefined;
});
$personalWorkspace.debugLabel = "personalWorkspace";

export const $workspacesByName = atom(async (get) => {
	const workspaces = await get($workspaces);
	if (!workspaces.isSuccess) {
		return undefined;
	}
	const workspacesByName: Record<string, Workspace> = {};
	for (const workspace of Object.values(workspaces.data)) {
		workspacesByName[workspace.name] = workspace;
	}
	return workspacesByName;
});

// This atom fetches workspace via workspaceName or workspaceId or workspaceSlug
export const $getWorkspace = atomFamily((workspaceNameOrId: string) => {
	const workspaceAtom = atom(async (get) => {
		const workspaces = await get($workspaces);
		if (!workspaces.isSuccess) {
			return undefined;
		}

		if (workspaceNameOrId.startsWith("wk-")) {
			return workspaces.data[workspaceNameOrId];
		}

		if (workspaceNameOrId.startsWith("github|")) {
			return workspaces.data[workspaceNameOrId];
		}

		if (workspaceNameOrId.startsWith("github-")) {
			const sanitanizeId = workspaceNameOrId.replace("github-", "github|");
			return workspaces.data[sanitanizeId];
		}

		// If workspaceNameOrId does not have these prefix above,
		// it is probably a workspace name
		const workspacesByName = await get($workspacesByName);
		return workspacesByName ? workspacesByName[workspaceNameOrId] : undefined;
	});
	workspaceAtom.debugLabel = `workspace(${workspaceNameOrId})`;
	return workspaceAtom;
});
